import { FC, useMemo } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { FaTriangleExclamation } from "react-icons/fa6";
import { useSelector } from "react-redux";
import { Alert } from "reactstrap";

import { PAYMENT_OPTIONS } from "@constants/Bookings";
import { CONSTANTS } from "@constants/index";
import { DateFormats } from "@enums/shared.enum";
import { useAmenitySummary } from "@hooks/useAmenitySummary/useAmenitySummary";
import {
  getParticipantCount,
  POSTHOG_ENABLED,
  useTrackBookingDetails,
  useTrackPaymentStep,
} from "@hooks/usePosthog/usePosthog";
import { usePricing } from "@hooks/usePricing/usePricing";
import { PricingProps } from "@models/components/booking.model";
import { AmenityTranslation } from "@models/services/amenity";
import { RootState } from "@store/index";

import { PaymentMethodSelector } from "../PaymentMethodSelector/PaymentMethodSelector";
import { PriceSummary } from "../PriceSummary/PriceSummary";
const Pricing: FC<PricingProps> = ({
  amenitiesList,
  grandTotal,
  isFromDrawer = false,
  onGrandTotalChange,
  payment,
  selectedAmenitie,
}) => {
  const { getPriceByResourceSlot, priceLoader } = usePricing();
  const { i18n, t } = useTranslation();
  const { selectedPaymentMethod, setSelectedPaymentMethod } = payment;

  const currentLang = (i18n.language?.split("-")[CONSTANTS.DEFAULT_VALUE] ||
    CONSTANTS.ENGLISH_LANGUAGE) as keyof AmenityTranslation;

  const {
    bookingEditData,
    guestList,
    isEditBooking,
    paymentLoader,
    resourcePrices,
    selectedDate,
    selectedSlot,
    selectedTime,
  } = useSelector((State: RootState) => State.booking);

  const currency = resourcePrices?.currency || "";
  const vat = resourcePrices?.vat;

  const { resourceId, slot } = selectedSlot;

  const paymentOptions =
    resourcePrices?.prices?.[resourceId]?.paymentOptions ?? [];

  const { basePrice, guestPriceAmount, totalPriceWithTax, vatAmount } =
    useMemo(() => {
      const {
        guestPrice = 0,
        price = 0,
        totalPriceWithTax = 0,
        vatAmount = 0,
      } = getPriceByResourceSlot(resourcePrices, resourceId, slot?.slot) || {};

      const numPrice = Number(price);
      const numGuestPrice = Number(guestPrice);

      return {
        basePrice: isNaN(numPrice) ? 0 : numPrice,
        guestPriceAmount: isNaN(numGuestPrice) ? 0 : numGuestPrice,
        totalPriceWithTax,
        vatAmount,
      };
    }, [resourcePrices, resourceId, slot?.slot, getPriceByResourceSlot]);

  const amenitySummary = useAmenitySummary({
    amenitiesList,
    bookingEditData,
    currentLang,
    isEditBooking,
    resourceId,
    resourcePrices,
    selectedAmenitie,
    slot,
  });

  const disablePaymentOption = !grandTotal || paymentLoader || priceLoader;

  const paymentAlert = useMemo(() => {
    if (!isEditBooking || bookingEditData?.totalPrice === undefined)
      return null;

    const totalAmount = grandTotal as number;
    const paymentType = bookingEditData?.payment?.type;
    const isPriceDecreased = totalAmount < bookingEditData.totalPrice;
    const isPriceIncreased = totalAmount > bookingEditData.totalPrice;
    if (bookingEditData.totalPrice > 0 && totalAmount == 0) {
      return { color: "danger", text: "bookingEdit.payableSlotToFreeWarning" };
    }
    if (paymentType === PAYMENT_OPTIONS.INSTANT_PAYMENT && isPriceDecreased) {
      return { color: "danger", text: "bookingEdit.instantPaymentWarning" };
    }

    if (
      paymentType === PAYMENT_OPTIONS.CASH &&
      (isPriceIncreased || isPriceDecreased)
    ) {
      return { color: "info", text: "bookingEdit.cashPaymentInfo" };
    }

    if (paymentType === PAYMENT_OPTIONS.INVOICE && isPriceDecreased) {
      return { color: "info", text: "bookingEdit.invoicePaymentDecreasedInfo" };
    }

    if (paymentType === PAYMENT_OPTIONS.INVOICE && isPriceIncreased) {
      return { color: "info", text: "bookingEdit.invoicePaymentIncreasedInfo" };
    }
    return null;
  }, [isEditBooking, bookingEditData, totalPriceWithTax, grandTotal]);

  const isLateBooking = useMemo(() => {
    if (isEditBooking || !selectedDate || !selectedTime) return false;
    const slotDateTime = moment(
      `${selectedDate}T${selectedTime}`,
      DateFormats.YMDTime
    );
    return slotDateTime.isBefore(moment());
  }, [selectedDate, selectedTime, isEditBooking]);

  const participantCount = useMemo(
    () =>
      POSTHOG_ENABLED
        ? getParticipantCount({
            editParticipants: bookingEditData?.participants,
            guestList,
          })
        : 0,
    [bookingEditData?.participants, guestList]
  );

  useTrackBookingDetails({
    bookingId: bookingEditData?.bookingId,
    grandTotal,
    participantCount,
    priceLoader,
  });

  useTrackPaymentStep({
    bookingId: bookingEditData?.bookingId,
    grandTotal,
    paymentOptions,
    priceLoader,
    selectedPaymentMethod,
  });

  if (paymentLoader && priceLoader) {
    return (
      <div className="fg-tab-unit-list">
        <div className="text-center py-4">
          <p>{t("loadingPricing")}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {typeof grandTotal === "number" && grandTotal > 0 && (
        <PaymentMethodSelector
          paymentOptions={paymentOptions}
          selectedPaymentMethod={selectedPaymentMethod}
          disablePaymentOption={disablePaymentOption}
          onSelectPaymentMethod={setSelectedPaymentMethod}
          grandTotal={grandTotal}
        />
      )}
      {paymentAlert && (
        <Alert color={paymentAlert.color} className="small p-2 mb-3 mt-2 ps-5">
          <FaTriangleExclamation className="fg-payment-method-alert-icon" />
          {t(paymentAlert.text)}
        </Alert>
      )}
      {isLateBooking && (
        <div
          className="alert alert-warning small p-2 mb-3"
          data-testid="late-booking-warning"
        >
          {t(
            "lateBookingWarning",
            "The selected time slot has already started."
          )}
          {typeof grandTotal === "number" && grandTotal > 0 && (
            <>
              {" "}
              {t(
                "lateBookingWarningPricing",
                "Normal pricing rules will apply."
              )}
            </>
          )}
        </div>
      )}
      <PriceSummary
        basePrice={basePrice}
        guestPrice={guestPriceAmount}
        vatAmount={vatAmount}
        totalPriceWithTax={totalPriceWithTax}
        currency={currency}
        vatDetails={vat}
        isFromDrawer={isFromDrawer}
        isEditBooking={isEditBooking}
        bookingEditData={bookingEditData}
        priceLoader={priceLoader}
        amenities={amenitySummary}
        onGrandTotalChange={onGrandTotalChange}
      />
    </>
  );
};

export default Pricing;
